import React, { useEffect, useState } from 'react';
import { Button, Select, Tooltip } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';

type EditableSelectProps = {
  defaultValue?: string;
  options: { label: string; value: string }[];
  onSave: (text: string) => void;
  validation?: (text: string) => string;
  ariaLabel?: string;
  render?: (text: string) => React.ReactNode;
  defaultIcon?: React.ReactNode;
  disabled?: boolean;
};

const EditableSelect: React.FC<EditableSelectProps> = ({
  defaultValue,
  options,
  onSave,
  validation,
  ariaLabel,
  render,
  defaultIcon,
  disabled,
}: EditableSelectProps) => {
  const [isEditing, setEditing] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(defaultValue);

  const icon = defaultIcon || <EditOutlined />;

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleSaveClick = async () => {
    if (validation && validation(selectedOption)) {
      return;
    }
    if (selectedOption !== defaultValue) {
      setLoading(true);
      onSave(selectedOption);
      setLoading(false);
    }
    setEditing(false);
  };

  const handleInputChange = (value: string) => {
    setSelectedOption(value);
  };

  const handleBlur = (event?) => {
    if (event?.relatedTarget?.type === 'button') {
      return;
    }
    setEditing(false);
    setSelectedOption(defaultValue);
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleBlur();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  return (
    <div className="w-full">
      {isEditing ? (
        <div>
          <Select
            defaultValue={selectedOption}
            style={{ width: 140 }}
            disabled={isLoading || disabled}
            options={options}
            onChange={handleInputChange}
            onBlur={handleBlur}
          />
          <Tooltip
            title={validation && validation(selectedOption)}
            open={validation && !!validation(selectedOption)}
          >
            <Button
              type="default"
              shape="circle"
              icon={<CheckOutlined style={{ fontSize: '14px' }} />}
              onClick={handleSaveClick}
              loading={isLoading}
            />
          </Tooltip>
        </div>
      ) : (
        <div className="flex items-center justify-end">
          {render ? render(selectedOption) : <span>{selectedOption}</span>}
          <Button
            type="link"
            icon={icon}
            onClick={handleEditClick}
            aria-label={ariaLabel}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default EditableSelect;
